import React from 'react';
import { getLatestFxRates, FxLatestResponse } from '../services/fxRatesService';
import { useAppSession } from '../contexts/AppSessionContext';

// Module-level cache: once loaded, serves all hook instances instantly
const fxCache: Record<string, FxLatestResponse> = {};

// Listeners that get notified when cache is populated
const fxListeners: Record<string, Set<(res: FxLatestResponse) => void>> = {};

// Single in-flight load per base currency (not a Promise — just a flag)
const fxLoading: Record<string, boolean> = {};

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

/** Kick off a load if one isn't already running. Notifies all listeners on success. */
async function ensureFxLoaded(base: string) {
  if (fxCache[base] || fxLoading[base]) return;
  fxLoading[base] = true;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      }
      const res = await getLatestFxRates(base);
      fxCache[base] = res;
      fxListeners[base]?.forEach((cb) => cb(res));
      fxLoading[base] = false;
      return;
    } catch (e: any) {
      console.warn(`[useFxRates] Attempt ${attempt + 1}/${MAX_RETRIES + 1} failed for ${base}:`, e?.message);
    }
  }
  console.error(`[useFxRates] ❌ Failed to load FX rates for ${base} after all retries`);
  fxLoading[base] = false;
}

export function useFxRates(base: string = 'USD') {
  const normalizedBase = base.toUpperCase();
  const { firebaseReady } = useAppSession();

  // Lazy-init from cache so components that mount after cache is set get data on first render
  const [data, setData] = React.useState<FxLatestResponse | null>(
    () => fxCache[normalizedBase] || null,
  );
  const [error] = React.useState<string | null>(null);

  React.useEffect(() => {
    const key = normalizedBase;

    // Don't try to fetch until Firebase auth is ready
    if (!firebaseReady) return;

    // If already cached (e.g. loaded by another component), set immediately
    if (fxCache[key]) {
      setData(fxCache[key]);
      return;
    }

    // Subscribe to cache population
    if (!fxListeners[key]) fxListeners[key] = new Set();
    const listener = (res: FxLatestResponse) => setData(res);
    fxListeners[key].add(listener);

    // Trigger load (no-op if already loading)
    ensureFxLoaded(key);

    return () => {
      fxListeners[key]?.delete(listener);
    };
  }, [normalizedBase, firebaseReady]);

  const getRateToBase = React.useCallback(
    (currency: string): number | null => {
      if (!data) return null;

      const normalized = currency.toUpperCase();
      if (normalized === data.base.toUpperCase()) return 1;

      const rate = data.rates[normalized];
      return typeof rate === 'number' ? rate : null;
    },
    [data],
  );

  return {
    ready: data !== null,
    base: data?.base || base,
    asOf: data?.asOf || null,
    rates: data?.rates || {},
    getRateToBase,
    error,
  };
}
